<script>
import { isEmpty } from 'lodash'
import RoomTypeForm from '@src/forms/RoomType'
import Request from '@src/mixins/request'
import staticDd from '@src/data/dropdown.json'
import multiRequest from '@utils/multi-request'

export default {
  $_veeValidate: { validator: 'new' },

  mixins: [Request],

  props: {
    propertyId: {
      type: [String, Number],
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      form: new RoomTypeForm(),
      dropdown: {},
      staticDd,
      color: 'gold',
      schema: RoomTypeForm.validator(),
      deleteDialog: false,
    }
  },

  computed: {
    text() {
      if (isEmpty(this.item)) {
        return { heading: 'New RoomType', submit: 'Add' }
      }

      return { heading: 'Edit RoomType', submit: 'Update' }
    },
    fileUploadOpts() {
      return {
        url: '/api' + this.endpoint.roomType.addImage,
        addRemoveLinks: true,
        paramName: 'image',
      }
    },
    images() {
      return isEmpty(this.item) ? [] : this.item.images
    },
  },

  mounted() {
    this.fetchDropdown()
    this.form = new RoomTypeForm(this.item)
  },

  methods: {
    async fetchDropdown() {
      const ddEndpoint = this.endpoint.dropdown
      this.dropdown = await multiRequest([
        {
          key: 'bedTypes',
          url: ddEndpoint.bedTypes,
        },
        {
          key: 'roomFacilities',
          url: ddEndpoint.roomFacilities,
        },
        {
          key: 'smokingPreferences',
          url: ddEndpoint.smokingPreferences,
        },
      ])
    },
    onSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (isEmpty(this.item)) {
            this.addItem()
          } else {
            this.updateItem()
          }
        }
      })
    },
    addItem() {
      this.updateData({
        url: this.endpoint.roomType.add,
        data: {
          propertyId: this.propertyId,
          data: this.form,
        },
      }).then(() => {
        this.backToList()
      })
    },
    updateItem() {
      this.updateData({
        url: this.endpoint.roomType.update,
        data: {
          id: this.item.id,
          propertyId: this.propertyId,
          update: this.form,
        },
      }).then(() => {
        this.backToList()
      })
    },
    backToList() {
      this.$emit('event', { currentView: 'RoomTypeList' })
    },
    async deleteImage(index) {
      await this.updateData({
        url: this.endpoint.roomType.delImage,
        data: {
          id: this.item.id,
          index,
        },
      })
      this.item.images.splice(index, 1)
      this.deleteDialog = false
    },
  },
}
</script>

<template>
  <VForm @submit.prevent="onSubmit">
    <BaseView :title="text.heading" :color="color">
      <VLayout row wrap>
        <!-- Basic Info -->
        <VFlex xs12 class="mb-4">
          <div class="title mb-2">Basic Info</div>

          <BaseControlInput label="Name">
            <VTextField
              v-model="form.name"
              v-validate="schema.name"
              data-vv-name="name"
              label="Name"
              :error-messages="errors.collect('name')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Description">
            <VTextarea
              v-model="form.description"
              v-validate="schema.description"
              data-vv-name="description"
              label="Description"
              :error-messages="errors.collect('description')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="No. of rooms">
            <VTextField
              v-model="form.numberOfRooms"
              v-validate="schema.numberOfRooms"
              data-vv-name="no. of rooms"
              label="No. of rooms"
              :error-messages="errors.collect('no. of rooms')"
              :color="color"
            />
          </BaseControlInput>
        </VFlex>
        <!-- End of Basic Info -->

        <!-- Occupancy Settings -->
        <VFlex xs12 sm6 class="mb-4">
          <div class="title mb-2">Occupancy Settings</div>

          <BaseControlInput label="Persons">
            <VAutocomplete
              v-model="form.persons"
              v-validate="schema.persons"
              data-vv-name="persons"
              label="Persons"
              :error-messages="errors.collect('persons')"
              :color="color"
              :items="staticDd.persons"
            />
          </BaseControlInput>

          <BaseControlInput label="Extra beds">
            <VAutocomplete
              v-model="form.extraBeds"
              v-validate="schema.extraBeds"
              data-vv-name="extra beds"
              label="Extra beds"
              :error-messages="errors.collect('extra beds')"
              :color="color"
              :items="staticDd.extraBeds"
            />
          </BaseControlInput>

          <VSubheader>
            Total persons allowed in this room:
            {{ form.persons + form.extraBeds }}
          </VSubheader>

          <BaseControlInput label="Minimum rate">
            <VTextField
              v-model.number="form.minimumRate"
              v-validate="schema.minimumRate"
              data-vv-name="minimum rate"
              label="Minimum rate"
              prefix="USD"
              :error-messages="errors.collect('minimum rate')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Rack rate">
            <VTextField
              v-model.number="form.rackRate"
              v-validate="schema.rackRate"
              data-vv-name="rack rate"
              label="Rack rate"
              prefix="USD"
              :error-messages="errors.collect('rack rate')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Extra bed rate">
            <VTextField
              v-model.number="form.extraBedRate"
              v-validate="schema.extraBedRate"
              data-vv-name="extra bed rate"
              label="Extra bed rate"
              prefix="USD"
              :error-messages="errors.collect('extra bed rate')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Total children">
            <VAutocomplete
              v-model="form.totalChildren"
              v-validate="schema.totalChildren"
              data-vv-name="total children"
              label="Total children"
              :error-messages="errors.collect('total children')"
              :color="color"
              :items="staticDd.numberOfChildren"
            />
          </BaseControlInput>

          <BaseControlInput label="Child bed rate">
            <VTextField
              v-model.number="form.childBedRate"
              v-validate="schema.childBedRate"
              data-vv-name="child bed rate"
              label="Child bed rate"
              prefix="USD"
              :error-messages="errors.collect('child bed rate')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Allotment alert">
            <VTextField
              v-model.number="form.allotmentAlert"
              v-validate="schema.allotmentAlert"
              data-vv-name="allotment alert"
              label="Allotment alert"
              :error-messages="errors.collect('allotment alert')"
              :color="color"
            />
          </BaseControlInput>
        </VFlex>
        <!-- End of Occupancy Settings -->

        <!-- Content -->
        <VFlex xs12 sm6 class="mb-4">
          <div class="title mb-2">Content</div>

          <BaseControlInput label="Room size">
            <VTextField
              v-model="form.size"
              v-validate="schema.size"
              data-vv-name="room size"
              label="Room size"
              suffix="sq.m"
              :error-messages="errors.collect('room size')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Size include terrace">
            <VAutocomplete
              v-model="form.sizeIncludeTerrace"
              v-validate="schema.sizeIncludeTerrace"
              data-vv-name="size include terrace"
              label="Size include terrace"
              :error-messages="errors.collect('size include terrace')"
              :color="color"
              :items="staticDd.switch"
            />
          </BaseControlInput>

          <BaseControlInput label="View">
            <VTextField
              v-model="form.view"
              v-validate="schema.view"
              data-vv-name="view"
              label="View"
              :error-messages="errors.collect('view')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Facilities">
            <VSelect
              v-model="form.facilities"
              v-validate="schema.facilities"
              data-vv-name="facilities"
              label="Facilities"
              :error-messages="errors.collect('facilities')"
              :color="color"
              :items="dropdown.roomFacilities"
              item-text="name"
              item-value="id"
              multiple
            >
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="grey--text caption">
                  {{ form.facilities.length }} selected
                </span>
              </template>
            </VSelect>
          </BaseControlInput>

          <BaseControlInput label="Bed type">
            <VAutocomplete
              v-model="form.bedType"
              v-validate="schema.bedType"
              data-vv-name="bed type"
              label="Bed type"
              :error-messages="errors.collect('bed type')"
              :color="color"
              :items="dropdown.bedTypes"
              item-text="name"
              item-value="id"
            />
          </BaseControlInput>

          <BaseControlInput label="Bathroom">
            <VTextField
              v-model="form.bathroom"
              v-validate="schema.bathroom"
              data-vv-name="bathroom"
              label="Bathroom"
              :error-messages="errors.collect('bathroom')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Wi-Fi included">
            <VAutocomplete
              v-model="form.wifiIncluded"
              v-validate="schema.wifiIncluded"
              data-vv-name="wi-fi included"
              label="Wi-Fi included"
              :error-messages="errors.collect('wi-fi included')"
              :color="color"
              :items="staticDd.switch"
            />
          </BaseControlInput>

          <BaseControlInput label="Smoking preference">
            <VAutocomplete
              v-model="form.smokingPreference"
              v-validate="schema.smokingPreference"
              data-vv-name="smoking preference"
              label="Smoking preference"
              :error-messages="errors.collect('smoking preference')"
              :color="color"
              :items="dropdown.smokingPreferences"
              item-text="name"
              item-value="id"
            />
          </BaseControlInput>
        </VFlex>
        <!-- End of Content -->

        <!-- Photos -->
        <VFlex xs12>
          <div class="title mb-2">Photos</div>

          <BaseGallery
            uploader
            :color="color"
            :images="images"
            :uploader-options="fileUploadOpts"
            :delete-dialog.sync="deleteDialog"
            :additional-params="{ id: item.id }"
            @delete-img="deleteImage"
          />
        </VFlex>
        <!-- End of Photos -->
      </VLayout>

      <template slot="card-actions-right">
        <VBtn
          depressed
          :disabled="loading"
          @click="$emit('event', { currentView: 'RoomTypeList' })"
        >
          Cancel
        </VBtn>

        <VBtn
          :color="color"
          :loading="loading"
          :disabled="!!errors.count()"
          type="submit"
          depressed
          :dark="!errors.count()"
        >
          {{ text.submit }}
        </VBtn>
      </template>
    </BaseView>
  </VForm>
</template>
